import { useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';

const defaultOptions = {
  enabled: false,
};

/**
 * Hook para sincronizar scroll entre el scrollbar superior espejo y el contenedor real.
 */
const useSyncScroll = (entryOptions) => {
  const options = _.merge({}, defaultOptions, entryOptions);

  const topScrollRef = useRef(null);
  const tableContainerRef = useRef(null);
  const innerRef = useRef(null);
  const isSyncingTop = useRef(false);
  const isSyncingBottom = useRef(false);

  const syncMirrorWidth = useCallback(() => {
    if (innerRef.current && topScrollRef.current) {
      topScrollRef.current.firstChild.style.width = `${innerRef.current.scrollWidth}px`;
    }
  }, []);

  useEffect(() => {
    if (!options.enabled) return undefined;

    const topEl = topScrollRef.current;
    const bottomEl = tableContainerRef.current;

    if (!topEl || !bottomEl) return undefined;

    const handleTopScroll = () => {
      if (isSyncingBottom.current) return;
      isSyncingTop.current = true;
      bottomEl.scrollLeft = topEl.scrollLeft;
      isSyncingTop.current = false;
    };

    const handleBottomScroll = () => {
      if (isSyncingTop.current) return;
      isSyncingBottom.current = true;
      topEl.scrollLeft = bottomEl.scrollLeft;
      isSyncingBottom.current = false;
    };

    topEl.addEventListener('scroll', handleTopScroll);
    bottomEl.addEventListener('scroll', handleBottomScroll);

    return () => {
      topEl.removeEventListener('scroll', handleTopScroll);
      bottomEl.removeEventListener('scroll', handleBottomScroll);
    };
  }, [options.enabled]);

  // Efecto que observa cambios de tamaño en <Table> para mantener
  // el ancho del div espejo siempre sincronizado (columnas dinámicas, resize de ventana, etc.).
  // También se omite cuando `enabled` es false.
  useEffect(() => {
    if (!options.enabled) return undefined;

    const inner = innerRef.current;
    if (!inner) return undefined;

    const resizeObserver = new ResizeObserver(syncMirrorWidth);
    resizeObserver.observe(inner);
    // sincronización inicial
    syncMirrorWidth();

    return () => resizeObserver.disconnect();
  }, [options.enabled, syncMirrorWidth]);

  return { topScrollRef, tableContainerRef, innerRef, syncMirrorWidth };
};

export default useSyncScroll;
